import {
  ChangeDetectionStrategy,
  Component,
  computed,
  OnDestroy,
} from '@angular/core';

import { AtomicUserComponent } from '../../private/abstract-components/atomic/atomic.user.component';
import { AVATAR_TO_NAME_RATIO } from '../../private/config';
import { generateCompositeSize } from '../../private/utils/utils';

/** Displays an entity by its avatar and username.
 * Shares the same inputs as the `UserNameComponent` and `UserAvatarComponent`.
 *
 * Note:
 * - The `detailedTitle` input setting will only apply
 * to the `UserNameComponent`.
 * - The `size` input applies for the `UserNameComponent` font size. Avatar sizes are calculated automatically.
 * - Projected content is displayed last.
 *
 * Example:
 * ```html
 * <plugins-user-entity>
 *  <span class="delete-action">x</span>
 * </plugins-user-entity />
 * ```*/
@Component({
  selector: 'plugins-user-entity',
  standalone: false,
  // eslint-disable-next-line @angular-eslint/component-max-inline-declarations
  template: `
    <div class="entity" [attr.data-userid]="dataUserId()">
      <div
        class="entity__name"
        *pluginsUserProfileLink="
          dataUserId();
          enabled: profileLinkEnabled() && user()?.type === 'user'
        "
      >
        <plugins-user-avatar
          [size]="avatarSize()"
          [userProvider]="userSignal()"
          [showTitle]="false"
          [profileLinkEnabled]="false"
        />
        <plugins-user-name
          [size]="size()"
          [userProvider]="userSignal()"
          [showTitle]="showTitle()"
          [detailedTitle]="detailedTitle()"
          [profileLinkEnabled]="false"
        />
      </div>
      <ng-content />
    </div>
  `,
  styleUrl: './user.entity.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserEntityComponent
  extends AtomicUserComponent
  implements OnDestroy
{
  /** We are  fetching users in the component in order to be able
   * to pass the userSignal to the child components directly while
   * ensuring the initial pending state is being shown. */
  private fetchUsers = this.user$.subscribe();
  readonly avatarSize = computed(() =>
    generateCompositeSize(this.size(), AVATAR_TO_NAME_RATIO),
  );
  ngOnDestroy(): void {
    this.fetchUsers.unsubscribe();
  }
}
